import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import api, { apiError } from "@/lib/api";
import { useInvalidate } from "@/lib/hooks";
import { Field } from "@/components/common";
import { FmcsaLookup } from "@/components/FmcsaLookup";
import { TRUCK_TYPES, SUITABLE_PLANS, leadEmpty } from "@/lib/leadConfig";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Buildings, IdentificationCard, Target, PaperPlaneRight, ArrowRight, ArrowLeft, Check, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";

const STEPS = [
  { icon: Buildings, title: "Carrier Company", hint: "Who are we recruiting?" },
  { icon: IdentificationCard, title: "Authority & Fleet", hint: "DOT authority and equipment" },
  { icon: Target, title: "Opportunity", hint: "Plan fit & conversion outlook" },
];

// Lightweight success chime via Web Audio API (no asset needed)
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.12;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      o.start(t); o.stop(t + 0.4);
    });
  } catch (e) { console.debug("Audio cue unavailable", e); }
}

function fireConfettiFor(ms) {
  const end = Date.now() + ms;
  const colors = ["#2563eb", "#f59e0b", "#10b981", "#ffffff", "#ef4444"];
  (function frame() {
    confetti({ particleCount: 5, angle: 60, spread: 70, startVelocity: 55, origin: { x: 0, y: 0.9 }, colors });
    confetti({ particleCount: 5, angle: 120, spread: 70, startVelocity: 55, origin: { x: 1, y: 0.9 }, colors });
    confetti({ particleCount: 3, angle: 90, spread: 100, startVelocity: 45, origin: { x: 0.5, y: 0 }, colors, gravity: 0.8 });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

export default function SubmitLead() {
  const [form, setForm] = useState(leadEmpty);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const invalidate = useInvalidate();
  const navigate = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTruck = (t) => setForm((f) => ({ ...f, truck_types: f.truck_types.includes(t) ? f.truck_types.filter((x) => x !== t) : [...f.truck_types, t] }));

  const next = () => {
    if (step === 0 && !form.company.trim()) { toast.error("Trucking company name is required"); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!form.company.trim()) { toast.error("Trucking company name is required"); setStep(0); return; }
    setSaving(true);
    try {
      await api.post("/leads", { ...form, estimated_value: Number(form.estimated_value) || 0, num_trucks: Number(form.num_trucks) || 0, expected_gross_weekly: Number(form.expected_gross_weekly) || 0, conversion_possibility: Number(form.conversion_possibility) || 0, assigned_to: null });
      invalidate(["leads", "dashboard", "my-activity", "leads-funnel"]);
      // Celebrate for 7 seconds, then redirect
      setCelebrate(true);
      playChime();
      fireConfettiFor(7000);
      setTimeout(() => navigate("/leads"), 7000);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setSaving(false); }
  };

  const inp = "bg-white border-slate-300 h-11 text-base";
  const Icon = STEPS[step].icon;

  return (
    <div className="max-w-4xl mx-auto" data-testid="submit-lead-fullscreen">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="font-heading font-extrabold text-2xl text-slate-900 dark:text-white leading-none">Submit Lead</p>
          <p className="text-sm text-blue-500 font-semibold mt-1">Onboard a new carrier prospect</p>
        </div>
        <button onClick={() => navigate("/leads")} className="text-sm font-semibold text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors" data-testid="sl-close">Cancel</button>
      </div>

      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-slate-200 dark:border-neutral-800 shadow-sm p-5 sm:p-8">
        {/* Stepper */}
        <div className="flex items-center mb-10">
          {STEPS.map((s, i) => {
            const SIcon = s.icon;
            const done = i < step, active = i === step;
            return (
              <React.Fragment key={i}>
                <button onClick={() => i <= step && setStep(i)} className="flex items-center gap-3 shrink-0" data-testid={`sl-step-${i}`}>
                  <div className={`w-12 h-12 rounded-full grid place-items-center transition-all ${active ? "bg-blue-600 text-white ring-4 ring-blue-100 scale-110" : done ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-400"}`}>
                    {done ? <Check size={20} weight="bold" /> : <SIcon size={20} weight="duotone" />}
                  </div>
                  <div className="hidden sm:block text-left"><p className={`text-sm font-bold ${active || done ? "text-slate-900" : "text-slate-400"}`}>{s.title}</p><p className="text-[11px] text-slate-400">{s.hint}</p></div>
                </button>
                {i < STEPS.length - 1 && <div className={`flex-1 h-1 mx-4 rounded-full transition-colors ${done ? "bg-blue-600" : "bg-slate-200"}`} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Big step heading */}
        <div className="flex items-center gap-4 mb-8 animate-fade-up" key={`h-${step}`}>
          <div className="w-14 h-14 rounded-2xl bg-blue-600 text-white grid place-items-center shadow-lg shadow-blue-600/25"><Icon size={28} weight="duotone" /></div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-blue-500">Step {step + 1} of {STEPS.length}</p>
            <h2 className="text-3xl font-heading font-extrabold text-slate-900 leading-tight">{STEPS[step].title}</h2>
          </div>
        </div>

        {/* Step content — only the active step is rendered */}
        <div className="animate-fade-up" key={`s-${step}`}>
          {step === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2"><FmcsaLookup testid="sl-fmcsa" onSelect={(r) => setForm((f) => ({ ...f, company: r.legal_name || f.company, phone: r.phone || f.phone, email: r.email || f.email, address: r.address || f.address, mc_number: r.mc_number || f.mc_number, usdot: r.usdot || f.usdot, num_trucks: r.power_units || f.num_trucks }))} /></div>
              <div className="sm:col-span-2"><Field label="Name of Trucking Company *"><Input data-testid="sl-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={inp} placeholder="e.g. Blue Line Carriers LLC" /></Field></div>
              <Field label="Owner / Manager"><Input data-testid="sl-owner" value={form.owner_manager} onChange={(e) => set("owner_manager", e.target.value)} className={inp} /></Field>
              <Field label="Phone"><Input data-testid="sl-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} /></Field>
              <Field label="Email"><Input data-testid="sl-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} /></Field>
              <Field label="Source"><Input value={form.source} onChange={(e) => set("source", e.target.value)} className={inp} placeholder="Cold call, referral…" /></Field>
              <div className="sm:col-span-2"><Field label="Address"><Input data-testid="sl-address" value={form.address} onChange={(e) => set("address", e.target.value)} className={inp} /></Field></div>
            </div>
          )}
          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="MC Number"><Input data-testid="sl-mc" value={form.mc_number} onChange={(e) => set("mc_number", e.target.value)} className={`${inp} font-mono`} /></Field>
              <Field label="USDOT Number"><Input data-testid="sl-usdot" value={form.usdot} onChange={(e) => set("usdot", e.target.value)} className={`${inp} font-mono`} /></Field>
              <div className="sm:col-span-2"><Field label="Type of Truck">
                <div className="flex flex-wrap gap-2">
                  {TRUCK_TYPES.map((t) => {
                    const on = form.truck_types.includes(t);
                    return <button key={t} type="button" data-testid={`sl-truck-${t.replace(/\s+/g, "-")}`} onClick={() => toggleTruck(t)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400 hover:text-blue-600"}`}>{t}</button>;
                  })}
                </div>
              </Field></div>
              <Field label="No. of Trucks"><Input type="number" data-testid="sl-numtrucks" value={form.num_trucks} onChange={(e) => set("num_trucks", e.target.value)} className={`${inp} font-mono`} /></Field>
              <Field label="Expected Gross Weekly / Truck ($)"><Input type="number" data-testid="sl-gross" value={form.expected_gross_weekly} onChange={(e) => set("expected_gross_weekly", e.target.value)} className={`${inp} font-mono`} /></Field>
            </div>
          )}
          {step === 2 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2"><Field label="Requirement of Carrier"><Textarea data-testid="sl-requirement" value={form.carrier_requirement} onChange={(e) => set("carrier_requirement", e.target.value)} className="bg-white border-slate-300 text-base" rows={3} placeholder="Lanes, rate expectations, dispatch needs…" /></Field></div>
              <Field label="Suitable Plan">
                <Select value={form.suitable_plan || ""} onValueChange={(v) => set("suitable_plan", v)}>
                  <SelectTrigger className={inp} data-testid="sl-plan"><SelectValue placeholder="Choose a plan" /></SelectTrigger>
                  <SelectContent>{SUITABLE_PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Additional Package Needed?">
                <div className="flex gap-2">
                  {["Yes", "No"].map((opt) => {
                    const on = form.additional_package === (opt === "Yes");
                    return <button key={opt} type="button" data-testid={`sl-addon-${opt}`} onClick={() => set("additional_package", opt === "Yes")} className={`flex-1 h-11 rounded-lg text-sm font-semibold border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}>{opt}</button>;
                  })}
                </div>
              </Field>
              <div className="sm:col-span-2"><Field label={`Lead Conversion Possibility — ${form.conversion_possibility}%`}>
                <input type="range" min="0" max="100" step="5" value={form.conversion_possibility} onChange={(e) => set("conversion_possibility", Number(e.target.value))} className="w-full accent-blue-600 cursor-pointer" data-testid="sl-conversion" />
                <div className="flex justify-between text-[11px] text-slate-400 mt-1"><span>Cold</span><span>Warm</span><span>Hot</span></div>
              </Field></div>
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="flex justify-between items-center mt-12 pt-6 border-t border-slate-200">
          <Button variant="outline" onClick={back} disabled={step === 0} className="border-slate-300 bg-white text-slate-700 disabled:opacity-40 h-11 px-6" data-testid="sl-back"><ArrowLeft size={16} className="mr-1.5" />Back</Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold h-11 px-8" data-testid="sl-next">Next<ArrowRight size={16} className="ml-1.5" /></Button>
          ) : (
            <Button onClick={submit} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold h-11 px-8" data-testid="sl-submit"><PaperPlaneRight size={17} weight="fill" className="mr-1.5" />{saving ? "Submitting…" : "Submit Lead"}</Button>
          )}
        </div>
      </div>

      {/* Celebration overlay */}
      {celebrate && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-900/70 backdrop-blur-sm" data-testid="lead-celebration">
          <div className="text-center px-6 animate-fade-up">
            <div className="mx-auto w-28 h-28 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center shadow-2xl shadow-amber-500/40 animate-bounce">
              <Trophy size={60} weight="fill" className="text-white" />
            </div>
            <h1 className="mt-8 text-3xl sm:text-5xl font-heading font-black text-white leading-tight drop-shadow-lg">
              Thank you for Submitting the Lead
            </h1>
            <p className="mt-4 text-lg sm:text-2xl font-heading font-bold text-amber-300">
              Let's work hard to Win this Lead! 💪
            </p>
            <p className="mt-6 text-sm text-slate-300">Redirecting to Lead Management…</p>
          </div>
        </div>
      )}
    </div>
  );
}
